import { z } from 'zod';

const decimalString = z.string().regex(/^-?\d+(\.\d+)?$/, 'invalidDecimal');
const periodString = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'invalidPeriod');

export const JournalListFiltersSchema = z.object({
  dealId: z.string().trim().optional(),
  entryType: z.string().trim().optional(),
  period: periodString.optional(),
});

export type JournalListFilters = z.infer<typeof JournalListFiltersSchema>;

export const JournalLineSchema = z.object({
  id: z.string(),
  account_code: z.string(),
  debit_amount: decimalString,
  credit_amount: decimalString,
  currency: z.string(),
  description: z.string(),
});

export type JournalLine = z.infer<typeof JournalLineSchema>;

export const JournalEntrySchema = z.object({
  id: z.string(),
  deal_id: z.string(),
  entry_type: z.string(),
  status: z.string(),
  valuation_id: z.string().nullable(),
  reversal_of_id: z.string().nullable(),
  lines: z.array(JournalLineSchema),
  created_at: z.string(),
});

export type JournalEntry = z.infer<typeof JournalEntrySchema>;

export const PostingRuleSchema = z.object({
  product_type: z.string().nullable(),
  asset_class: z.string().nullable(),
  entry_type: z.string(),
  debit_account: z.string(),
  credit_account: z.string(),
});

export type PostingRule = z.infer<typeof PostingRuleSchema>;

export const SettlementEntryRequestSchema = z.object({
  deal_id: z.string().trim().min(1, 'required'),
  amount: z
    .string()
    .regex(/^\d+(\.\d+)?$/, 'invalidDecimal')
    .refine((v) => !/^0+(\.0+)?$/.test(v), { message: 'mustBePositive' }),
  currency: z
    .string()
    .trim()
    .min(3, 'required')
    .max(3, 'invalidCurrency')
    .regex(/^[A-Za-z]{3}$/, 'invalidCurrency')
    .transform((v) => v.toUpperCase()),
});

export type SettlementEntryRequest = z.infer<typeof SettlementEntryRequestSchema>;

export const PeriodCloseRequestSchema = z.object({
  period: periodString,
});

export type PeriodCloseRequest = z.infer<typeof PeriodCloseRequestSchema>;

export const JournalListPayloadSchema = z.object({
  filters: JournalListFiltersSchema.optional(),
});

export type JournalListPayload = z.infer<typeof JournalListPayloadSchema>;

export const JournalDetailPayloadSchema = z.object({
  entryId: z.string().min(1),
  sourceFilters: JournalListFiltersSchema.optional(),
});

export type JournalDetailPayload = z.infer<typeof JournalDetailPayloadSchema>;

export const PostSettlementEntryPayloadSchema = z.object({
  deal_id: z.string().optional(),
  amount: z.string().optional(),
  currency: z.string().optional(),
});

export type PostSettlementEntryPayload = z.infer<typeof PostSettlementEntryPayloadSchema>;

export const PeriodClosePayloadSchema = z.object({
  period: periodString.optional(),
});

export type PeriodClosePayload = z.infer<typeof PeriodClosePayloadSchema>;
